import { useEffect } from "react";

/**
 * Updates document head with title, description, OpenGraph and optional JSON-LD structured data.
 * Cleans up on unmount.
 */
export function useSEO({ title, description, image, url, type = "website", jsonLd }) {
  useEffect(() => {
    const prev = {
      title: document.title,
      meta: {},
      ld: null,
    };
    if (title) {
      document.title = title;
    }
    const ensure = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(attrs.tag || "meta");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "tag" && k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      prev.meta[selector] = { created, prev: el.getAttribute("content") };
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
      return el;
    };

    if (description) {
      ensure('meta[name="description"]', { name: "description", content: description });
      ensure('meta[property="og:description"]', { property: "og:description", content: description });
      ensure('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    if (title) {
      ensure('meta[property="og:title"]', { property: "og:title", content: title });
      ensure('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    if (image) {
      ensure('meta[property="og:image"]', { property: "og:image", content: image });
      ensure('meta[name="twitter:image"]', { name: "twitter:image", content: image });
      ensure('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    }
    if (url) {
      ensure('meta[property="og:url"]', { property: "og:url", content: url });
    }
    ensure('meta[property="og:type"]', { property: "og:type", content: type });
    ensure('meta[property="og:site_name"]', { property: "og:site_name", content: "Maggie Records" });

    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.textContent = JSON.stringify(jsonLd);
      ldEl.setAttribute("data-vmr-jsonld", "1");
      document.head.appendChild(ldEl);
      prev.ld = ldEl;
    }

    return () => {
      document.title = prev.title;
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
    };
  }, [title, description, image, url, type, JSON.stringify(jsonLd || null)]);
}
